import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { API_URI } from "../../helpers";

/**
 * Seguimiento automático: los toques que se le mandan a quien escribió, recibió la
 * autorespuesta y no dijo nada más. Se edita en caliente (tabla wa_followup_steps).
 *
 * La pantalla muestra el estado real del motor arriba de todo a propósito: sin eso
 * miente, porque se pueden editar textos preciosos mientras el motor está apagado y
 * no se manda nada. Ojo con la distinción: las AUTORESPUESTAS no dependen del motor
 * (salen directo del webhook), sólo el seguimiento pasa por el outbox.
 */
const useStyles = makeStyles({
  intro: { fontSize: 13, color: "#64748B", lineHeight: 1.6, marginBottom: 18 },
  banner: {
    display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px",
    borderRadius: 10, marginBottom: 18, fontSize: 13, lineHeight: 1.5,
  },
  bannerOff: { background: "#FEF3C7", border: "1px solid #FCD34D", color: "#78350F" },
  bannerOn: { background: "#DCFCE7", border: "1px solid #86EFAC", color: "#14532D" },
  bannerIcon: { fontSize: 18, lineHeight: "18px" },

  statsRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 },
  stat: {
    flex: "1 1 120px", background: "#F8FAFC", border: "1px solid #E2E8F0",
    borderRadius: 10, padding: "12px 14px",
  },
  statValue: { fontSize: 24, fontWeight: 700, color: "#0F172A", lineHeight: 1.1 },
  statLabel: { fontSize: 11, color: "#64748B", fontWeight: 600, marginTop: 3 },

  timeline: { position: "relative", paddingLeft: 26, marginBottom: 8 },
  tlLine: {
    position: "absolute", left: 7, top: 10, bottom: 10, width: 2, background: "#E2E8F0",
  },
  tlNode: { position: "relative", marginBottom: 14 },
  tlDot: {
    position: "absolute", left: -26, top: 16, width: 16, height: 16, borderRadius: "50%",
    background: "#fff", border: "3px solid #CBD5E1", boxSizing: "border-box",
  },
  tlDotActive: { borderColor: "#16A34A" },
  tlDotEdge: { borderColor: "#94A3B8", background: "#F1F5F9" },

  edge: { fontSize: 12, color: "#64748B", padding: "6px 0 14px 0", fontWeight: 600 },
  edgeNote: { fontWeight: 400, color: "#94A3B8", marginLeft: 6 },

  card: {
    border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, background: "#fff",
  },
  cardOff: { opacity: 0.55, background: "#FAFAFA" },
  cardHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  hoursWrap: { display: "flex", alignItems: "center", gap: 6 },
  hoursInput: {
    width: 62, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 8,
    fontSize: 14, textAlign: "center", outline: "none", boxSizing: "border-box",
  },
  hoursLabel: { fontSize: 12, color: "#64748B" },
  spacer: { flex: 1 },
  chip: {
    fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
    background: "#F1F5F9", color: "#475569",
  },
  chipSent: { background: "#DBEAFE", color: "#1E40AF" },
  textarea: {
    width: "100%", padding: "10px 12px", border: "1px solid #CBD5E1", borderRadius: 8,
    fontSize: 14, outline: "none", resize: "vertical", minHeight: 76,
    fontFamily: "inherit", boxSizing: "border-box", lineHeight: 1.5,
  },
  cardFoot: {
    display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap",
  },
  hint: { fontSize: 11, color: "#94A3B8" },
  btn: {
    padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8,
    cursor: "pointer", border: "1px solid #CBD5E1", background: "#fff", color: "#334155",
  },
  btnPrimary: { background: "#16A34A", borderColor: "#16A34A", color: "#fff" },
  btnDanger: { borderColor: "#FECACA", color: "#B91C1C" },
  btnDisabled: { opacity: 0.5, cursor: "default" },
  addBtn: {
    width: "100%", padding: "11px 16px", border: "1px dashed #16A34A", borderRadius: 10,
    cursor: "pointer", background: "#fff", color: "#16A34A", fontSize: 13, fontWeight: 600,
  },
  empty: {
    border: "1px dashed #CBD5E1", borderRadius: 10, padding: "26px 20px",
    textAlign: "center", color: "#64748B", fontSize: 13, marginBottom: 14, lineHeight: 1.6,
  },
  err: {
    background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#991B1B",
    borderRadius: 8, padding: "9px 12px", fontSize: 12, marginBottom: 12,
  },
  footNote: {
    fontSize: 12, color: "#94A3B8", lineHeight: 1.6, marginTop: 18,
    borderTop: "1px solid #F1F5F9", paddingTop: 14,
  },
});

/** Formatea horas como "3h" o "1h 30m", que se lee mejor que "1.5". */
function fmtHours(h) {
  const n = Number(h) || 0;
  const whole = Math.floor(n);
  const mins = Math.round((n - whole) * 60);
  if (mins === 0) return whole + "h";
  if (whole === 0) return mins + "m";
  return whole + "h " + mins + "m";
}

export default function FollowupSteps() {
  const classes = useStyles();
  const [steps, setSteps] = useState([]);
  const [engine, setEngine] = useState(null);
  const [funnel, setFunnel] = useState({});
  const [drafts, setDrafts] = useState({});   // id | "new" => {after_hours, text}
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    fetch(API_URI + "/admin/whatsapp/followup/steps")
      .then((r) => r.json())
      .then((res) => {
        if (res.result) {
          setSteps(res.data || []);
          setEngine(res.engine || {});
          setFunnel(res.funnel || {});
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };

  useEffect(load, []);

  const draftFor = (step) => {
    const d = drafts[step.id];
    return d || { after_hours: step.after_hours, text: step.text };
  };

  /**
   * `base` siembra el borrador con los valores actuales del toque la primera vez
   * que se toca. Sin eso, editar sólo el texto dejaba el borrador en
   * {text: "..."} y after_hours quedaba undefined: el input pasaba a no
   * controlado y al guardar se mandaba el campo vacío, que el backend rechaza.
   */
  const setDraft = (key, field, value, base) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(base || {}), ...(prev[key] || {}), [field]: value },
    }));
  };

  const dirty = (step) => {
    const d = drafts[step.id];
    if (!d) return false;
    return (
      String(d.after_hours) !== String(step.after_hours) || String(d.text) !== String(step.text)
    );
  };

  const post = (path, params) => {
    const body = new FormData();
    Object.keys(params).forEach((k) => body.append(k, params[k]));
    return fetch(API_URI + "/admin/whatsapp/followup/step/" + path, { method: "POST", body })
      .then((r) => r.json());
  };

  const save = (key, values, existingId) => {
    setError("");
    setSavingId(key);
    const params = {
      after_hours: values.after_hours,
      text: values.text,
      active: values.active === false ? "0" : "1",
    };
    if (existingId) params.id = existingId;

    post("save", params)
      .then((res) => {
        if (!res.result) {
          setError(res.error || "No se pudo guardar.");
        } else {
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          load();
        }
        setSavingId(null);
      })
      .catch(() => {
        setError("No se pudo guardar: falló la conexión con la API.");
        setSavingId(null);
      });
  };

  const toggle = (step) => {
    post("toggle", { id: step.id }).then(load);
  };

  const remove = (step) => {
    const msg =
      steps.filter((s) => s.active).length === 1 && step.active
        ? "Es el único toque activo: al borrarlo el seguimiento deja de mandar nada. ¿Seguir?"
        : "¿Eliminar este toque?";
    if (!window.confirm(msg)) return;
    post("delete", { id: step.id }).then(load);
  };

  if (!loaded) return <div style={{ padding: 20, color: "#94A3B8" }}>Cargando…</div>;

  const activeSteps = steps.filter((s) => s.active);
  const windowH = (engine && engine.window_hours) || 24;
  const marginMin = (engine && engine.window_margin_min) || 30;
  const on = engine && engine.messaging_enabled && engine.followup_enabled;
  const sent = funnel.by_step || {};

  // Motivo concreto por el que no está mandando, si aplica.
  let offReason = null;
  if (engine && !engine.messaging_enabled) {
    offReason = "El motor de mensajería está apagado (MESSAGING_ENABLED=false en el .env del server). " +
      "Podés editar los toques igual, pero no se manda nada. Ojo al prenderlo: ese flag es global y " +
      "activa también el recupero de carritos.";
  } else if (engine && !engine.followup_enabled) {
    offReason = "El seguimiento está apagado (MESSAGING_FOLLOWUP_ENABLED=false), aunque el motor de " +
      "mensajería sí está activo.";
  } else if (activeSteps.length === 0) {
    offReason = "No hay ningún toque activo, así que la secuencia no manda nada.";
  }

  return (
    <div>
      <div className={classes.intro}>
        Si alguien escribe por WhatsApp o por DM de Instagram/Messenger, recibe la autorespuesta y
        <strong> no dice nada más</strong>, le llegan estos toques. En cuanto contesta cualquier cosa
        la secuencia se corta para siempre y la conversación queda para un operador humano — que es
        justamente el objetivo. Nunca se le manda un toque a alguien que ya está siendo atendido por
        una persona.
      </div>

      <div className={`${classes.banner} ${offReason ? classes.bannerOff : classes.bannerOn}`}>
        <span className={classes.bannerIcon}>{offReason ? "⏸" : "▶"}</span>
        <span>
          <strong>{offReason ? "No está enviando. " : "Activo. "}</strong>
          {offReason || (
            <>
              La secuencia corre cada 15 minutos sobre los canales{" "}
              {(engine.channels || []).join(", ") || "—"}.
            </>
          )}
          {engine && engine.messaging_enabled && !engine.meta_autoreply_enabled && (
            <>
              {" "}Los DMs de Meta casi no van a generar candidatos: su autorespuesta está apagada
              (<code>META_AUTOREPLY_ENABLED=false</code>) y sin un saliente automático la secuencia
              no tiene de dónde engancharse.
            </>
          )}
        </span>
      </div>

      <div className={classes.statsRow}>
        <div className={classes.stat}>
          <div className={classes.statValue}>{funnel.touched || 0}</div>
          <div className={classes.statLabel}>CON SEGUIMIENTO</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statValue} style={{ color: "#16A34A" }}>{funnel.replied || 0}</div>
          <div className={classes.statLabel}>CONTESTARON</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statValue} style={{ color: "#16A34A" }}>{funnel.reply_rate || 0}%</div>
          <div className={classes.statLabel}>TASA DE RESPUESTA</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statValue}>{funnel.pending || 0}</div>
          <div className={classes.statLabel}>EN COLA</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statValue} style={{ color: funnel.failed ? "#DC2626" : "#0F172A" }}>
            {funnel.failed || 0}
          </div>
          <div className={classes.statLabel}>FALLIDOS</div>
        </div>
      </div>

      {error && <div className={classes.err}>{error}</div>}

      <div className={classes.timeline}>
        <div className={classes.tlLine} />

        <div className={classes.tlNode}>
          <div className={`${classes.tlDot} ${classes.tlDotEdge}`} />
          <div className={classes.edge}>
            0h — el cliente escribe y le sale la autorespuesta{" "}
            <span className={classes.edgeNote}>(se configura en la pestaña Autorespuesta)</span>
          </div>
        </div>

        {steps.length === 0 && (
          <div className={classes.empty}>
            <strong>Sin toques configurados.</strong>
            <br />
            La secuencia no manda nada. Agregá el primero acá abajo.
          </div>
        )}

        {steps.map((step, i) => {
          const d = draftFor(step);
          const isDirty = dirty(step);
          const busy = savingId === step.id;
          return (
            <div className={classes.tlNode} key={step.id}>
              <div className={`${classes.tlDot} ${step.active ? classes.tlDotActive : ""}`} />
              <div className={`${classes.card} ${step.active ? "" : classes.cardOff}`}>
                <div className={classes.cardHead}>
                  <strong style={{ fontSize: 14 }}>Toque {i + 1}</strong>
                  <div className={classes.hoursWrap}>
                    <input
                      className={classes.hoursInput}
                      type="number"
                      min="0.25"
                      max={windowH - 0.5}
                      step="0.25"
                      value={d.after_hours}
                      onChange={(e) =>
                        setDraft(step.id, "after_hours", e.target.value, {
                          after_hours: step.after_hours,
                          text: step.text,
                        })
                      }
                    />
                    <span className={classes.hoursLabel}>
                      h después del último mensaje del cliente
                    </span>
                  </div>
                  <span className={classes.spacer} />
                  {sent["step" + (i + 1)] > 0 && (
                    <span className={`${classes.chip} ${classes.chipSent}`}>
                      {sent["step" + (i + 1)]} enviados
                    </span>
                  )}
                  <span className={classes.chip}>{step.active ? "Activo" : "Pausado"}</span>
                </div>

                <textarea
                  className={classes.textarea}
                  value={d.text}
                  onChange={(e) =>
                    setDraft(step.id, "text", e.target.value, {
                      after_hours: step.after_hours,
                      text: step.text,
                    })
                  }
                  placeholder="Texto del mensaje…"
                />

                <div className={classes.cardFoot}>
                  <span className={classes.hint}>
                    {fmtHours(d.after_hours)} · admite <code>{"{nombre}"}</code> ·{" "}
                    {(d.text || "").length} caracteres
                  </span>
                  <span className={classes.spacer} />
                  <button
                    className={`${classes.btn} ${classes.btnPrimary} ${isDirty && !busy ? "" : classes.btnDisabled}`}
                    disabled={!isDirty || busy}
                    onClick={() => save(step.id, { ...d, active: step.active }, step.id)}
                  >
                    {busy ? "Guardando…" : isDirty ? "Guardar cambios" : "Guardado"}
                  </button>
                  <button className={classes.btn} onClick={() => toggle(step)}>
                    {step.active ? "Pausar" : "Activar"}
                  </button>
                  <button
                    className={`${classes.btn} ${classes.btnDanger}`}
                    onClick={() => remove(step)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <div className={classes.tlNode}>
          <div className={`${classes.tlDot} ${classes.tlDotEdge}`} />
          <div className={classes.edge}>
            {windowH}h — se cierra la ventana de Meta{" "}
            <span className={classes.edgeNote}>
              a partir de acá no se puede escribir más, aunque no haya contestado
            </span>
          </div>
        </div>
      </div>

      {drafts["new"] ? (
        <div className={classes.card} style={{ marginLeft: 26 }}>
          <div className={classes.cardHead}>
            <strong style={{ fontSize: 14 }}>Toque nuevo</strong>
            <div className={classes.hoursWrap}>
              <input
                className={classes.hoursInput}
                type="number"
                min="0.25"
                max={windowH - 0.5}
                step="0.25"
                value={drafts["new"].after_hours}
                onChange={(e) => setDraft("new", "after_hours", e.target.value)}
              />
              <span className={classes.hoursLabel}>h después del último mensaje del cliente</span>
            </div>
          </div>
          <textarea
            className={classes.textarea}
            value={drafts["new"].text}
            onChange={(e) => setDraft("new", "text", e.target.value)}
            placeholder="Texto del mensaje…"
            autoFocus
          />
          <div className={classes.cardFoot}>
            <span className={classes.hint}>
              admite <code>{"{nombre}"}</code>
            </span>
            <span className={classes.spacer} />
            <button
              className={`${classes.btn} ${classes.btnPrimary} ${
                (drafts["new"].text || "").trim() && savingId !== "new" ? "" : classes.btnDisabled
              }`}
              disabled={!(drafts["new"].text || "").trim() || savingId === "new"}
              onClick={() => save("new", drafts["new"], null)}
            >
              {savingId === "new" ? "Guardando…" : "Crear toque"}
            </button>
            <button
              className={classes.btn}
              onClick={() =>
                setDrafts((prev) => {
                  const n = { ...prev };
                  delete n["new"];
                  return n;
                })
              }
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          className={classes.addBtn}
          onClick={() => {
            // Por default, a mitad de camino entre el último toque y el cierre de ventana.
            const last = steps.length ? Number(steps[steps.length - 1].after_hours) : 0;
            const suggested = Math.min(Math.round((last + windowH) / 2), windowH - 1);
            setDrafts((prev) => ({ ...prev, new: { after_hours: suggested || 3, text: "" } }));
          }}
        >
          + Agregar un toque
        </button>
      )}

      <div className={classes.footNote}>
        <strong>Por qué hay un techo de {windowH}h:</strong> ni WhatsApp ni Messenger/Instagram
        aceptan texto libre pasadas {windowH}h del último mensaje del cliente — después haría falta
        una plantilla aprobada por Meta. El sistema deja {marginMin} minutos de margen contra ese
        borde, así que en la práctica la ventana útil termina a las{" "}
        {fmtHours(windowH - marginMin / 60)}.
        <br />
        <strong>Si un toque queda vencido</strong> porque la conversación se descubrió tarde, se
        manda sólo el más avanzado, no todos los anteriores en cadena. Y entre dos toques siempre
        pasan al menos {fmtHours((engine && engine.min_gap_hours) || 2)}.
      </div>
    </div>
  );
}
